import { Link } from "react-router-dom";
import {
  Briefcase, Users, ArrowRight, Phone, Mail, CheckCircle2, Handshake,
  Wallet, ShieldCheck, Quote, Megaphone, Layers, LifeBuoy, BarChart3, TrendingUp,
} from "lucide-react";
import { EditableText, useContentText } from "../lib/content";
import { useSeo } from "../lib/useSeo";
import TrustStatement from "../components/TrustStatement";

/* Helper - absolute URL for media served from /api/static. */
function absUrl(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  if (url.startsWith("/")) return `${base}${url}`;
  return url;
}

const PARTNERSHIP_BENEFITS = [
  "Strengthens your client or member offering",
  "Reduces naming risk across your network",
  "Improves retention and engagement",
  "Introduces optional recurring revenue",
  "Adds value without operational burden",
];

const MODELS = [
  {
    icon: Wallet,
    chip: "Revenue option",
    title: "Referral model",
    body: "Introduce Business Name Protection to your clients or members and receive recurring commission for each active subscription.",
    bullets: [
      "Simple referral process",
      "Fully managed by NBR Group",
      "No support or administration required from you",
      "Transparent reporting and tracking",
    ],
  },
  {
    icon: Handshake,
    chip: "Value-led option",
    title: "Member or client benefit model",
    body: "Offer Business Name Protection as an exclusive discounted service for your clients or members.",
    bullets: [
      "Strengthens your value proposition",
      "Improves retention and loyalty",
      "Positions your organisation as proactive and supportive",
      "No requirement to earn commission",
    ],
  },
  {
    icon: Layers,
    chip: "Advanced partners",
    title: "White-label or hybrid model",
    body: "Fully integrate Business Name Protection into your own offering.",
    bullets: [
      "Your own branding if required",
      "Ability to set your own pricing structure",
      "Combine member benefits with revenue generation",
      "Ideal for larger associations or advisory networks",
    ],
  },
];

const PARTNER_TYPES = [
  {
    icon: Briefcase,
    title: "For accountants",
    intro: "Add Business Name Protection as a simple extension of your client advisory offering.",
    bullets: [
      "Easy to introduce to existing clients",
      "Adds practical intellectual property protection without complexity",
      "Optional commission or client benefit model",
      "Co-branded onboarding materials available",
      "Dedicated UK account manager support",
    ],
    closing: "You can introduce it immediately with no operational workload.",
  },
  {
    icon: Users,
    title: "For trade associations and membership bodies",
    intro: "Give your members access to practical protection for their business name at an exclusive rate.",
    bullets: [
      "Member-only pricing structures available",
      "White-label or co-branded delivery options",
      "Ready to use webinars and content resources",
      "Full service delivery handled by our UK team",
      "Optional reporting insights for member engagement",
    ],
    closing: "It strengthens membership value while reinforcing your position as a trusted organisation.",
  },
];

const WHAT_YOU_GET = [
  {
    icon: TrendingUp,
    title: "Commercial flexibility",
    bullets: [
      "Choose commission, member benefit or hybrid models",
      "Optional volume based pricing improvements",
      "White-label pricing control available for larger partners",
    ],
  },
  {
    icon: LifeBuoy,
    title: "Full operational support",
    bullets: [
      "We manage onboarding and customer support",
      "We provide all client facing materials",
      "We handle ongoing service delivery",
      "No technical or legal setup required",
    ],
  },
  {
    icon: BarChart3,
    title: "Dedicated partner support",
    bullets: [
      "UK based account manager",
      "Sales and onboarding assistance",
      "Training and marketing collateral",
      "Co-branded assets where required",
    ],
  },
];

const CASE_STUDIES = [
  {
    quote: "NBR's Business Name Protection service has integrated seamlessly with our existing systems and provides an additional revenue stream. Their expert support ensures peace of mind for both us and our clients. We would recommend NBR's services to any accounting firm looking to enhance its offering.",
    name: "Daryl Davies",
    role: "Davies Archytas Accountants Ltd",
    photo: "/api/static/testimonials/daryl_davies.png",
  },
  {
    quote: "In our collaboration with NBR, they've already provided invaluable assistance to several clients. The team is always responsive, professional, and their Business Name Protection service has brought peace of mind to our clients. I'd highly recommend their services.",
    name: "Caitie Ford",
    role: "iLex Accounting Services Ltd",
    photo: "/api/static/testimonials/caitie_ford.png",
  },
];

export default function Partnerships() {
  const phone = useContentText("partnerships.contact.phone", "0800 069 9090");
  const phoneDigits = phone.replace(/\D/g, "");
  const email = useContentText("partnerships.contact.email", "info@nbrg.co.uk");

  useSeo({
    title: "Partnerships - Refer Business Name Protection",
    description: "Add a structured, affordable name-protection layer to your accountancy, association or business-services offering. Recurring commission, no build cost, no legal exposure.",
  });

  return (
    <main data-testid="partnerships-page">
      {/* ───────────── HERO ───────────── */}
      <section className="relative overflow-hidden bnp-radial-glow pt-14 md:pt-20 pb-12">
        <div className="absolute inset-0 bnp-grid-bg opacity-30 pointer-events-none" />
        <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24 relative text-center">
          <span className="bnp-chip bnp-chip-teal">Why partner with NBR Group</span>
          <h1 className="font-display text-[clamp(2rem,4vw,3.4rem)] text-[#04273d] mt-4 font-semibold tracking-tight leading-[1.08]">
            <EditableText
              keyName="partnerships.hero.title"
              default="Add value to your clients or members without adding complexity to your business"
            />
          </h1>
          <p className="text-[#447694] text-base md:text-lg mt-5 leading-relaxed">
            Most UK businesses assume their company name is protected when they register it.
          </p>
          <p className="font-display text-[#04273d] font-bold text-lg md:text-xl mt-3">
            It is not.
          </p>
          <p className="text-[#447694] text-base md:text-lg mt-3 leading-relaxed">
            Companies House only checks for identical names. It does not prevent similar or confusingly close names from being registered or used.
          </p>
          <p className="text-[#447694] text-base md:text-lg mt-3 leading-relaxed">
            That gap creates real risk for your clients or members. This includes brand confusion, lost work and costly rebranding exercises that can run into thousands of pounds.
          </p>
          <p className="text-[#04273d] text-base md:text-lg mt-5 font-semibold">
            By partnering with NBR Group, you can offer a practical solution that:
          </p>
          <ul className="mt-5 grid sm:grid-cols-2 gap-2.5 max-w-2xl mx-auto text-left">
            {PARTNERSHIP_BENEFITS.map((b, i) => {
              const isOrphan = i === PARTNERSHIP_BENEFITS.length - 1 && PARTNERSHIP_BENEFITS.length % 2 === 1;
              return (
                <li
                  key={b}
                  className={`flex items-start gap-2.5 text-sm md:text-base text-[#447694] bnp-card p-3 ${
                    isOrphan ? "sm:col-span-2 sm:w-[calc(50%-0.3125rem)] sm:justify-self-center" : ""
                  }`}
                  data-testid="partners-hero-benefit"
                >
                  <CheckCircle2 size={16} className="text-[#0d9b80] mt-0.5 shrink-0" />
                  <span>{b}</span>
                </li>
              );
            })}
          </ul>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <a href={`tel:${phoneDigits}`} className="btn-primary" data-testid="partners-hero-call-button">
              <Phone size={14} /> Call partnerships: {phone}
            </a>
            <a href={`mailto:${email}?subject=Partnership enquiry`} className="btn-secondary" data-testid="partners-hero-email-button">
              <Mail size={14} /> Email partnerships
            </a>
          </div>
        </div>
      </section>

      {/* ───────────── FLEXIBLE PARTNER MODELS ───────────── */}
      <section className="py-16 md:py-20 lg:py-24 xl:py-28 bg-white border-y border-[#9ac7f7]/25" data-testid="partners-models-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="bnp-chip bnp-chip-light">Flexible partner models</span>
            <h2 className="font-display text-[clamp(1.75rem,3.2vw,3rem)] text-[#04273d] mt-3 font-bold tracking-tight">
              Choose the approach that suits your organisation
            </h2>
            <p className="text-[#447694] mt-4 text-base md:text-lg leading-relaxed">
              Every organisation is different. Business Name Protection is designed to fit around your needs.
            </p>
          </div>
          <div className="grid lg:grid-cols-3 gap-5">
            {MODELS.map((m, i) => (
              <div key={i} className="bnp-card p-6 md:p-7 flex flex-col" data-testid={`partners-model-${i}`}>
                <div className="flex items-center justify-between">
                  <div className="h-11 w-11 rounded-xl bg-[#71edc9]/25 flex items-center justify-center">
                    <m.icon size={20} className="text-[#04273d]" />
                  </div>
                  <span className="bnp-chip bnp-chip-light text-[10px]">{m.chip}</span>
                </div>
                <h3 className="font-display text-xl md:text-2xl text-[#04273d] mt-4 font-bold tracking-tight">{m.title}</h3>
                <p className="text-sm md:text-base text-[#447694] mt-2 leading-relaxed">{m.body}</p>
                <ul className="mt-4 space-y-2 flex-1">
                  {m.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm text-[#447694]">
                      <CheckCircle2 size={14} className="text-[#0d9b80] mt-0.5 shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── BUILT FOR ADVISORS / ASSOCIATIONS ───────────── */}
      <section className="py-16 md:py-20 lg:py-24 xl:py-28" data-testid="partners-types-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <span className="bnp-chip bnp-chip-light">Who we work with</span>
            <h2 className="font-display text-[clamp(1.75rem,3.2vw,3rem)] text-[#04273d] mt-3 font-bold tracking-tight">
              Built for professional advisors and membership organisations
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {PARTNER_TYPES.map((p, i) => (
              <div key={i} className="bnp-card p-6 md:p-8 flex flex-col" data-testid={`partners-type-${i}`}>
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-xl bg-[#04273d] flex items-center justify-center">
                    <p.icon size={20} className="text-[#71edc9]" />
                  </div>
                  <h3 className="font-display text-xl md:text-2xl text-[#04273d] font-bold tracking-tight">{p.title}</h3>
                </div>
                <p className="text-[#447694] mt-4 leading-relaxed">{p.intro}</p>
                <ul className="mt-4 space-y-2 flex-1">
                  {p.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm text-[#447694]">
                      <CheckCircle2 size={14} className="text-[#0d9b80] mt-0.5 shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-[#04273d] font-semibold text-sm mt-5 leading-relaxed">{p.closing}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── WHAT YOU GET AS A PARTNER ───────────── */}
      <section className="py-16 md:py-20 lg:py-24 xl:py-28 bg-gradient-to-br from-[#f7f7f7] via-white to-[#71edc9]/10" data-testid="partners-what-you-get-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="bnp-chip bnp-chip-light">What you get as a partner</span>
            <h2 className="font-display text-[clamp(1.75rem,3.2vw,3rem)] text-[#04273d] mt-3 font-bold tracking-tight">
              Simple to launch and easy to maintain
            </h2>
            <p className="text-[#447694] mt-4 text-base md:text-lg leading-relaxed">
              We have designed the programme so you can offer Business Name Protection without adding complexity to your business.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {WHAT_YOU_GET.map((g, i) => (
              <div key={i} className="bnp-card p-6 md:p-7" data-testid={`partners-what-you-get-${i}`}>
                <div className="h-11 w-11 rounded-xl bg-[#71edc9]/25 flex items-center justify-center">
                  <g.icon size={20} className="text-[#04273d]" />
                </div>
                <h3 className="font-display text-lg md:text-xl text-[#04273d] mt-4 font-bold">{g.title}</h3>
                <ul className="mt-3 space-y-2">
                  {g.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm text-[#447694]">
                      <CheckCircle2 size={14} className="text-[#0d9b80] mt-0.5 shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── WHY THIS MATTERS TO CLIENTS ───────────── */}
      <section className="py-16 md:py-20 lg:py-24 xl:py-28" data-testid="partners-why-matters-section">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24">
          <div className="text-center mb-8">
            <span className="bnp-chip bnp-chip-light">Why this matters to your clients or members</span>
            <h2 className="font-display text-[clamp(1.75rem,3.2vw,3rem)] text-[#04273d] mt-3 font-bold tracking-tight">
              Practical protection without legal complexity
            </h2>
          </div>
          <div className="bnp-card p-6 md:p-8 space-y-4 text-[#447694] text-base md:text-lg leading-relaxed">
            <p>Your clients or members do not always need complex legal services.</p>
            <p>They need simple practical protection that helps prevent disruption later.</p>
            <p className="text-[#04273d] font-semibold">
              Business Name Protection sits between doing nothing and investing in a full trade mark application.
            </p>
            <p>It is designed for real UK businesses that rely on their trading name every day.</p>
          </div>
        </div>
      </section>

      {/* ───────────── WHAT PARTNERS ARE SAYING ───────────── */}
      <section className="py-16 md:py-20 lg:py-24 xl:py-28 bg-white border-y border-[#9ac7f7]/25" data-testid="partners-testimonials-section">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="bnp-chip bnp-chip-light">What partners are saying</span>
            <h2 className="font-display text-[clamp(1.75rem,3.2vw,3rem)] text-[#04273d] mt-3 font-bold tracking-tight">
              Real feedback from UK firms already working with us
            </h2>
            <p className="text-[#447694] mt-4 text-base md:text-lg leading-relaxed">
              We work closely with accountants and professional service firms across the UK who have integrated Business Name Protection into their offering.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {CASE_STUDIES.map((c, i) => (
              <figure key={i} className="bnp-card p-6 md:p-7 flex flex-col" data-testid={`partners-testimonial-${i}`}>
                <Quote size={22} className="text-[#71edc9]" />
                <blockquote className="text-[#04273d] text-base md:text-lg mt-3 leading-relaxed font-display flex-1">
                  {c.quote}
                </blockquote>
                <figcaption className="mt-5 pt-5 border-t border-[#9ac7f7]/30 flex items-center gap-3">
                  {c.photo ? (
                    <img
                      src={absUrl(c.photo)}
                      alt={c.name}
                      className="h-12 w-12 rounded-full object-cover border-2 border-[#9ac7f7]/30"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  ) : (
                    <div className="h-12 w-12 rounded-full bg-[#04273d]/10 flex items-center justify-center">
                      <Users size={18} className="text-[#04273d]" />
                    </div>
                  )}
                  <div>
                    <p className="font-display font-bold text-[#04273d]">{c.name}</p>
                    <p className="text-xs text-[#447694] font-medium">{c.role}</p>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
          <p className="text-center text-[#447694] mt-8 text-sm md:text-base leading-relaxed max-w-3xl mx-auto">
            These partnerships are built on simplicity, responsiveness and practical value for both advisers and their clients.
          </p>
        </div>
      </section>

      {/* ───────────── SPEAK TO PARTNERSHIPS + CONTACT ───────────── */}
      <section className="py-16 md:py-20 lg:py-24 xl:py-28" data-testid="partners-contact-section">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="bnp-chip bnp-chip-light">Speak to our partnerships team</span>
            <h2 className="font-display text-[clamp(1.75rem,3.2vw,3rem)] text-[#04273d] mt-3 font-bold tracking-tight">
              Let us build a model that works for your organisation
            </h2>
            <p className="text-[#447694] mt-4 text-base md:text-lg leading-relaxed">
              Whether you want to introduce a client or member benefit, generate recurring revenue, or build a fully white-label offering, we will help you design the right approach.
            </p>
            <p className="text-[#447694] mt-3 text-base md:text-lg leading-relaxed">
              You will receive clear onboarding, full support and a dedicated UK team from day one.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <a
              href={`tel:${phoneDigits}`}
              className="bnp-card p-6 md:p-7 flex items-start gap-4 hover:-translate-y-0.5 transition-transform"
              data-testid="partners-contact-call"
            >
              <div className="h-12 w-12 rounded-2xl bg-[#04273d] flex items-center justify-center shrink-0">
                <Phone size={20} className="text-[#71edc9]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-mono-brand text-[10.5px] uppercase tracking-widest text-[#447694] font-semibold">Call partnerships</p>
                <p className="font-display text-xl md:text-2xl text-[#04273d] mt-1 font-bold">{phone}</p>
                <p className="text-xs text-[#447694] mt-1.5">Mon to Fri, 9am to 5:30pm</p>
              </div>
              <ArrowRight size={14} className="text-[#04273d] shrink-0 mt-2" />
            </a>
            <a
              href={`mailto:${email}?subject=Partnership enquiry`}
              className="bnp-card p-6 md:p-7 flex items-start gap-4 hover:-translate-y-0.5 transition-transform"
              data-testid="partners-contact-email"
            >
              <div className="h-12 w-12 rounded-2xl bg-[#04273d] flex items-center justify-center shrink-0">
                <Mail size={20} className="text-[#71edc9]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-mono-brand text-[10.5px] uppercase tracking-widest text-[#447694] font-semibold">Email partnerships</p>
                <p className="font-display text-base md:text-lg text-[#04273d] mt-1 font-bold break-all">{email}</p>
                <p className="text-xs text-[#447694] mt-1.5">We respond within 2 working days</p>
              </div>
              <ArrowRight size={14} className="text-[#04273d] shrink-0 mt-2" />
            </a>
          </div>
        </div>
      </section>

      {/* ───────────── FINAL CTA ───────────── */}
      <section className="py-16 md:py-20 lg:py-24 xl:py-28 bg-[#04273d] text-white" data-testid="partners-final-cta">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24 text-center">
          <Megaphone size={32} className="text-[#71edc9] mx-auto" />
          <h2 className="font-display text-[clamp(1.75rem,3.2vw,3rem)] mt-4 font-bold tracking-tight leading-[1.08]">
            Ready to explore this properly
          </h2>
          <p className="text-[#9ac7f7] mt-4 text-base md:text-lg leading-relaxed">
            Before anything is set up, we will walk you through how the service works, how partners use it and which model is best suited to your organisation.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href={`tel:${phoneDigits}`}
              className="inline-flex items-center justify-center gap-2 bg-[#71edc9] text-[#04273d] hover:bg-[#71edc9]/85 font-display font-bold px-6 py-3.5 rounded-full transition-colors w-full sm:w-auto"
              data-testid="partners-final-cta-call"
            >
              Speak to the partnerships team <ArrowRight size={14} />
            </a>
            <a
              href={`mailto:${email}?subject=Explore partnership options`}
              className="inline-flex items-center justify-center gap-2 bg-white text-[#04273d] hover:bg-[#f7f7f7] font-display font-bold px-6 py-3.5 rounded-full transition-colors w-full sm:w-auto"
              data-testid="partners-final-cta-email"
            >
              Explore partnership options <ArrowRight size={14} />
            </a>
          </div>
          <p className="text-[11.5px] text-[#9ac7f7] mt-6 font-mono-brand uppercase tracking-widest">
            UK based team &middot; No setup fees &middot; Dedicated account manager
          </p>
          <TrustStatement className="mt-5" theme="dark" />
        </div>
      </section>
    </main>
  );
}
